export type PageValues = Record<string, unknown>;
export function isCompanyDestination(values: PageValues) {
  const account = values.accountTarget as { companyId?: string; userId?: string } | null | undefined;
  if (account) return Boolean(account.companyId && !account.userId);
  return values.view === 'Admin' && Boolean(values['admin.selectedContractorId']) && !values['admin.selectedUserId'];
}
type Entry = { siteAuditNavigation: string; index: number; values: PageValues; scroll: number };
export type HistoryHost = {
  history: Pick<History, 'state' | 'pushState' | 'replaceState' | 'go'>;
  location: { href: string };
  scrollY: number;
  scrollTo: (options: ScrollToOptions) => void;
};

// One entry per user action, including actions that update several view fields.
// Only page selections belong here; passwords, form content and files do not.
export class NavigationHistory {
  values: PageValues = {};
  private index = 0;
  private listeners = new Set<() => void>();
  private queued = false;
  private reverting: (() => void) | null = null;
  private desiredScroll = 0;
  private guard: (() => Promise<boolean>) | null = null;
  private permission: Promise<boolean> | null = null;
  constructor(private host: HistoryHost, private identity: string) {}
  start() { this.host.history.replaceState(this.entry(), '', this.host.location.href); }
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  get<T>(key: string, initial: T): T { return Object.prototype.hasOwnProperty.call(this.values, key) ? this.values[key] as T : initial; }
  setGuard(guard: (() => Promise<boolean>) | null) { this.guard = guard; }
  private notify() { this.listeners.forEach((listener) => listener()); }
  private entry(): Entry { return { siteAuditNavigation: this.identity, index: this.index, values: this.values, scroll: this.host.scrollY }; }
  private allowed() {
    if (!this.guard) return Promise.resolve(true);
    if (!this.permission) this.permission = this.guard().finally(() => { this.permission = null; });
    return this.permission;
  }
  async proceed(action: () => void | Promise<void>) { if (await this.allowed()) await action(); }
  // Explicit destinations reset nested selections together and retain one Back step.
  async visit(values: PageValues) {
    if (!await this.allowed()) return;
    if (Object.entries(values).every(([key, value]) => Object.is(this.values[key], value))) {
      this.desiredScroll = 0; this.restoreScroll(); return;
    }
    this.commit(values);
  }
  rememberScroll() {
    const current = this.host.history.state as Entry | null;
    if (!this.queued && current?.siteAuditNavigation === this.identity && current.index === this.index) this.host.history.replaceState(this.entry(), '', this.host.location.href);
  }
  async set<T>(key: string, update: T | ((value: T) => T), initial: T, mode: 'push' | 'replace' = 'push') {
    if (typeof update !== 'function' && Object.is(this.get(key, initial), update)) return;
    if (!await this.allowed()) return;
    const previous = this.get(key, initial);
    const value = typeof update === 'function' ? (update as (value: T) => T)(previous) : update;
    if (Object.is(previous, value)) return;
    this.commit({ [key]: value }, mode);
  }
  private commit(values: PageValues, mode: 'push' | 'replace' = 'push') {
    if (!this.queued) this.host.history.replaceState(this.entry(), '', this.host.location.href);
    this.values = { ...this.values, ...values }; this.notify();
    if (mode === 'replace') { this.host.history.replaceState(this.entry(), '', this.host.location.href); return; }
    if (this.queued) return;
    this.queued = true;
    queueMicrotask(() => {
      this.queued = false; this.index += 1; this.desiredScroll = 0;
      this.host.history.pushState({ ...this.entry(), scroll: 0 }, '', this.host.location.href);
      this.restoreScroll();
    });
  }
  // Initial loading and access revocation replace the current screen, not history.
  replace(values: PageValues) { this.values = { ...this.values, ...values }; this.host.history.replaceState(this.entry(), '', this.host.location.href); this.notify(); }
  back(fallback: () => void) { if (this.index > 0) this.host.history.go(-1); else fallback(); }
  async pop(state: unknown) {
    const target = state as Entry | null;
    if (!target || target.siteAuditNavigation !== this.identity) return;
    if (this.reverting) { const complete = this.reverting; this.reverting = null; complete(); return; }
    if (target.index === this.index) return;
    if (this.guard) {
      const delta = target.index - this.index;
      await new Promise<void>((resolve) => { this.reverting = resolve; this.host.history.go(-delta); });
      if (await this.allowed()) {
        // The guard has saved/discarded the draft. The following traversal is safe.
        this.guard = null; this.host.history.go(delta);
      }
      return;
    }
    this.index = target.index; this.values = target.values; this.desiredScroll = isCompanyDestination(target.values) ? 0 : target.scroll;
    this.notify(); this.restoreScroll();
  }
  restoreScroll() {
    this.host.scrollTo({ top: this.desiredScroll, behavior: 'instant' });
  }
}
